import "../../polyfills";
import "../../base.css";
import "../../turoUiFonts.css";

import { Pedal } from "@turo/pedal";
import type { DriverAccountResponse } from "@turo/types-dunlop";
import { TuroUi } from "@turo/ui";
import { Fragment } from "react";
import { createRoot } from "react-dom/client";

import {
  loginStart,
  loginSuccess,
  otpTokenLoginStart,
} from "~/src/helpers/loginActions";

import {
  dispatch,
  getPropertiesFromQuery,
  getTranslationsLocale,
} from "../../helpers/iframeHelpers";
import SimpleTranslateProvider from "../../SimpleTranslateProvider";
import commonEnUsTranslations from "../locales/en_US.json";
import commonEsUsTranslations from "../locales/es_US.json";
import commonFrCaTranslations from "../locales/fr_CA.json";
import commonFrFrTranslations from "../locales/fr_FR.json";
import commonPtBrTranslations from "../locales/pt_BR.json";
import otpLoginEmailEnUsTranslations from "../otp-login-email/locales/en_US.json";
import otpLoginEmailEsUsTranslations from "../otp-login-email/locales/es_US.json";
import otpLoginEmailFrCaTranslations from "../otp-login-email/locales/fr_CA.json";
import otpLoginEmailFrFrTranslations from "../otp-login-email/locales/fr_FR.json";
import otpLoginEmailPtBrTranslations from "../otp-login-email/locales/pt_BR.json";
import type {
  SignupErrorResponse,
  SignupSuccessResponse,
} from "./EmailSignupForm";
import EmailSignupFormView from "./EmailSignupFormView";
import enUsTranslations from "./locales/en_US.json";
import esUsTranslations from "./locales/es_US.json";
import frCaTranslations from "./locales/fr_CA.json";
import frFrTranslations from "./locales/fr_FR.json";
import ptBrTranslations from "./locales/pt_BR.json";
import { signupFailure, signupStart, signupSuccess } from "./SignupActions";

export type SignupTranslations = typeof enUsTranslations &
  typeof otpLoginEmailEnUsTranslations &
  typeof commonEnUsTranslations;

const {
  hideMarketingSection,
  isOptinMatrixEnabled,
  locale,
  marketingSmsOptIn,
  partnerReservationId,
  referrer,
} = getPropertiesFromQuery();

// @ts-expect-error -- TODO-TS: Add full type-narrowing handling in getPropertiesFromQuery
const translationsLocale = getTranslationsLocale(locale);

const mappedPropertiesToDispatch = {
  // ==========Signup Flow===================
  signupFailure: (data: SignupErrorResponse) => dispatch(signupFailure(data)),
  signupStart: () => dispatch(signupStart()),
  signupSuccess: (data: SignupSuccessResponse) => dispatch(signupSuccess(data)),
  // ==========Login Flow=====================
  // if the user email is already in use, we go to login by OTP flow
  loginStart: () => dispatch(loginStart()),
  loginSuccess: (data: DriverAccountResponse) => dispatch(loginSuccess(data)),
  otpTokenLoginStart: () => dispatch(otpTokenLoginStart()),
};

function renderPage(translations: SignupTranslations) {
  const container = document.querySelector("#container");

  /**
   * This is a type guard to satisfying the type definition of `document.querySelector`,
   * but our `index.html` file has a `#container` element, so this will never throw.
   *
   * Common guidance is to use casting (`as Element`) or the non-null assertion operator
   * (`document.querySelector("#container")!`), but both patterns should be discouraged
   * in favor of runtime guards.
   */
  if (!container) {
    throw new Error("Could not find #container element");
  }

  const root = createRoot(container);

  root.render(
    <Fragment>
      <TuroUi>
        {/*
        // @ts-expect-error -- Add runtime type narrowing for locale in getPropertiesFromQuery */}
        <Pedal locale={locale}>
          <SimpleTranslateProvider translations={translations}>
            <EmailSignupFormView
              {...mappedPropertiesToDispatch}
              hideMarketingSection={hideMarketingSection}
              isOptinMatrixEnabled={isOptinMatrixEnabled}
              marketingSmsOptIn={marketingSmsOptIn}
              partnerReservationId={partnerReservationId}
              referrer={referrer}
            />
          </SimpleTranslateProvider>
        </Pedal>
      </TuroUi>
    </Fragment>,
  );
}

let translations;
switch (translationsLocale) {
  case "es_US": {
    translations = {
      ...commonEsUsTranslations,
      ...esUsTranslations,
      ...otpLoginEmailEsUsTranslations,
    };
    break;
  }
  case "pt_BR": {
    translations = {
      ...commonPtBrTranslations,
      ...ptBrTranslations,
      ...otpLoginEmailPtBrTranslations,
    };
    break;
  }
  case "fr_CA": {
    translations = {
      ...commonFrCaTranslations,
      ...frCaTranslations,
      ...otpLoginEmailFrCaTranslations,
    };
    break;
  }
  case "fr_FR": {
    translations = {
      ...commonFrFrTranslations,
      ...frFrTranslations,
      ...otpLoginEmailFrFrTranslations,
    };
    break;
  }
  // eslint-disable-next-line unicorn/no-useless-switch-case -- en_US case not required, but keeping for visibility
  case "en_US":
  default: {
    translations = {
      ...commonEnUsTranslations,
      ...enUsTranslations,
      ...otpLoginEmailEnUsTranslations,
    };
  }
}

renderPage(translations);
